import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { EmployeeSizeRange } from "@/types/api";
import { DescriptionSummary } from "./DescriptionSummary";
import { StepFooter } from "./StepFooter";

const INDUSTRIES = [
  "Logistics",
  "Manufacturing",
  "IT",
  "Retail",
  "Construction",
  "Healthcare",
  "Finance",
  "Automotive",
  "Food & Beverage",
  "Energy",
];

interface IndustryStepProps {
  description: string;
  industry: string;
  onIndustryChange: (val: string) => void;
  employeeSize: EmployeeSizeRange;
  onEmployeeSizeChange: (val: EmployeeSizeRange) => void;
  aiSuggested?: boolean;
  onBack: () => void;
  onContinue: () => void;
  onSkipToReview: () => void;
}

export function IndustryStep({
  description,
  industry,
  onIndustryChange,
  employeeSize,
  onEmployeeSizeChange,
  aiSuggested = false,
  onBack,
  onContinue,
  onSkipToReview,
}: IndustryStepProps) {
  const { t } = useTranslation("search");
  const [inputValue, setInputValue] = useState(industry);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = inputValue.trim()
    ? INDUSTRIES.filter((ind) =>
        ind.toLowerCase().includes(inputValue.toLowerCase()),
      )
    : INDUSTRIES;

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setInputValue(val);
    onIndustryChange(val);
    setIsOpen(true);
  }

  function handleSelect(val: string) {
    setInputValue(val);
    onIndustryChange(val);
    setIsOpen(false);
  }

  function handleBlur() {
    // Delay close so click on dropdown item registers first
    setTimeout(() => setIsOpen(false), 150);
  }

  function handleFocus() {
    setIsOpen(true);
  }

  return (
    <div className="space-y-0">
      <DescriptionSummary description={description} />

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            {t("wizard.industryStep.cardTitle")}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-5">
          {/* Industry autocomplete */}
          <div>
            <Label
              htmlFor="wizard-industry"
              className="text-xs font-medium mb-1.5 block"
            >
              {t("wizard.industryStep.industryLabel")}
            </Label>
            <div ref={containerRef} className="relative">
              <Input
                id="wizard-industry"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder={t("wizard.industryStep.industryPlaceholder")}
                autoComplete="off"
              />
              {isOpen && filtered.length > 0 && (
                <ul className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md max-h-52 overflow-auto py-1">
                  {filtered.map((ind) => (
                    <li
                      key={ind}
                      onMouseDown={(e) => {
                        e.preventDefault(); // prevent blur firing first
                        handleSelect(ind);
                      }}
                      className={cn(
                        "cursor-pointer px-3 py-2 text-sm",
                        ind === inputValue
                          ? "bg-muted text-foreground font-medium"
                          : "hover:bg-muted/50",
                      )}
                    >
                      {ind}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {aiSuggested && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground">
                <Sparkles className="h-3 w-3" />
                {t("wizard.industryStep.aiSuggested")}
              </p>
            )}
          </div>

          {/* Employee size */}
          <div>
            <Label
              htmlFor="wizard-employee-size"
              className="text-xs font-medium mb-1.5 block"
            >
              {t("wizard.industryStep.companySizeLabel")}
            </Label>
            <Select
              value={employeeSize}
              onValueChange={(val) =>
                onEmployeeSizeChange(val as EmployeeSizeRange)
              }
            >
              <SelectTrigger id="wizard-employee-size">
                <SelectValue placeholder={t("wizard.industryStep.anySize")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">
                  {t("wizard.industryStep.sizes.any")}
                </SelectItem>
                <SelectItem value="1-9">
                  {t("wizard.industryStep.sizes.micro")}
                </SelectItem>
                <SelectItem value="10-49">
                  {t("wizard.industryStep.sizes.small")}
                </SelectItem>
                <SelectItem value="50-249">
                  {t("wizard.industryStep.sizes.medium")}
                </SelectItem>
                <SelectItem value="250-999">
                  {t("wizard.industryStep.sizes.large")}
                </SelectItem>
                <SelectItem value="1000+">
                  {t("wizard.industryStep.sizes.enterprise")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <StepFooter
        onBack={onBack}
        onContinue={onContinue}
        onSkipToReview={onSkipToReview}
      />
    </div>
  );
}
